import * as React from "react";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";
import Loading from "../ui/loading/Loading";
import {none, option, Option} from "ts-option";
import DataGrid from "../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import {api} from "../../api/api";
import CurrencyHelper from "../../helper/CurrencyHelper";
import {CommonDetailsStatusRow} from "./common/CommonDetailsStatusRow";
import {default as CommonDetailsInfoRow, DetailsInfoRowItem} from "./common/CommonDetailsInfoRow";
import Transaction, {BankAccountType, ReconciliationStatus} from "../../model/transaction/Transaction";
import DateUtils from "../../utils/DateUtils";
import TransactionActivityStream from "./transactionActivityStream/TransactionActivityStream";
import {TransactionEventType} from "../../model/transaction/event/TransactionEvent";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import AccountingNote from "../../model/invoice/note/AccountingNote";
import TransactionActivityTabs from "./transactionActivityTabs/TransactionActivityTabs";
import ReconciliationModal from "../v5/table/presenter/reconciliation/ReconciliationModal";
import TransactionStatusPresenter from "./TransactionStatusPresenter";
import BillReconciliationModal from "../v5/table/presenter/reconciliation/BillReconciliationModal";
import Routes from "../../navigation/route/Route";

interface RouteProps {
	/**
	 * Transaction internal identifier.
	 */
	transactionId: string;
}

interface State {
	transaction: Option<Transaction>;
	/**
	 * Transaction activity events.
	 */
	events: TransactionEventType[];
	/**
	 * Transaction notes.
	 */
	notes: AccountingNote[];
	/**
	 * Note selected for edition.
	 */
	noteToEdit: Option<AccountingNote>;
	/**
	 * Reconciliation modal visibility indicator.
	 */
	reconciliationModalVisible: boolean;
}

type Props = RouteComponentProps<RouteProps>;

/**
 * Transaction details view.
 */
class TransactionDetailsView extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {transaction: none, events: [], notes: [], noteToEdit: none, reconciliationModalVisible: false};
	}

	componentDidMount(): void {
		this.fetchTransaction();
	}

	fetchTransaction = () => api.instance.transactionApi.fetchTransaction(this.props.match.params.transactionId).then(
		transaction => {
			this.setState({transaction: option(transaction)});
			this.fetchTransactionEvents(transaction.id);
			this.fetchTransactionNotes(transaction.id);
		}).catch(error => {
		notifyBugsnagAndShowToast(error, "Fetch transaction");
		this.props.history.goBack();
	});

	fetchTransactionEvents = (transactionId: string) => {
		return api.instance.transactionApi.fetchTransactionEvents(transactionId).catch(error => {
			notifyBugsnagAndShowToast(error, "Fetch transaction events");
			throw error;
		}).then(events => this.setState({events}));
	};

	fetchTransactionNotes = (transactionId: string) => {
		return api.instance.noteApi.fetchTransactionNotes(transactionId).catch(error => {
			notifyBugsnagAndShowToast(error, "Fetch transaction notes");
			throw error;
		}).then(notes => this.setState({notes}));
	};

	render() {
		return (
			<div className={"l-page"}>
				<Loading isLoading={this.state.transaction.isEmpty}>
					<CommonDetailsStatusRow title={"Transaction"}
											onBackClick={this.goBack}
											refNumPath={["All transactions", "Transaction"]}
											additionalComponent={this.renderStatus()}/>
					{this.renderDetailsInfoRow()}
					{this.renderTable()}
					<div className="c-common-details_activity-tabs-container">
						<TransactionActivityTabs transaction={this.state.transaction}
												 noteToEdit={this.state.noteToEdit}
												 onAddNoteSuccess={this.onAddNoteSuccess}
												 onUpdateNoteSuccess={this.onUpdateNoteSuccess}/>
					</div>
					{this.state.transaction.map(t => <TransactionActivityStream transaction={t}
																				activeNoteId={this.props.location.state ?
																					this.props.location.state.activeId : ""}
																				events={this.state.events}
																				notes={this.state.notes}
																				onEditNoteClick={this.onEditNoteClick}
																				onDeleteNoteSuccess={this.onDeleteNoteSuccess}/>).orNull}
					{this.state.reconciliationModalVisible && this.state.transaction.map(t => {
						const ModalComponent = t.bankAccountType === BankAccountType.TRUST_ACCOUNT ? ReconciliationModal :
							BillReconciliationModal;
						return <ModalComponent transaction={t}
													onClose={this.closeReconciliationModal}
													onReconciliationSuccess={this.fetchTransaction}
													readOnly={t.reconciliationStatus === ReconciliationStatus.RECONCILED}/>
					}).orNull}
				</Loading>
			</div>
		);
	}

	goBack = () => {
		if (this.props.location.state) {
			this.props.history.push(Routes.AllTransactionsView, {pageNo: this.props.history.location.state["pageNo"]});
		} else {
			this.props.history.push(Routes.AllTransactionsView);
		}
	};

	renderDetailsInfoRow = () => {
		const detailsInfoRowItem1: DetailsInfoRowItem = {
			label: "Name",
			value: this.state.transaction.map(t => t.name).orNull
		};
		const detailsInfoRowItem2: DetailsInfoRowItem = {
			label: "Account #",
			value: this.state.transaction.flatMap(t => t.accountNo).orNull
		};
		const detailsInfoRowItem3: DetailsInfoRowItem = {
			label: "Branch code",
			value: this.state.transaction.flatMap(t => t.branchCode).orNull
		};
		const detailsInfoRowItem4: DetailsInfoRowItem = {
			label: "Comment",
			value: this.state.transaction.flatMap(t => t.comment).orNull
		};
		const detailsInfoRowItem5: DetailsInfoRowItem = {
			label: "Time",
			value: this.state.transaction.map(t => t.date.format(DateUtils.DATE_FORMAT)).orNull
		};
		return <CommonDetailsInfoRow items={[
			detailsInfoRowItem1, detailsInfoRowItem2, detailsInfoRowItem3, detailsInfoRowItem4, detailsInfoRowItem5
		]}/>
	};

	renderTable = () => {
		return (
			<DataGrid>
				<thead>
				<tr className="c-common-details_table-header-row">
					<th className="c-common-details_table-desc-cell">DESCRIPTION</th>
					<th>TOTAL</th>
				</tr>
				</thead>
				<tbody>
				{this.state.transaction.map(t =>
					<tr className="c-common-details_table-row">
						<td className="c-common-details_table-desc-cell">Payment</td>
						<td>{CurrencyHelper.toCurrency(t.amount)}</td>
					</tr>).orNull}
				</tbody>
			</DataGrid>);
	};

	renderStatus = () => this.state.transaction.map(t =>
		<TransactionStatusPresenter transaction={t} onClick={this.openReconciliationModal}/>).orNull;

	openReconciliationModal = () => this.setState({reconciliationModalVisible: true});

	closeReconciliationModal = () => this.setState({reconciliationModalVisible: false});

	onAddNoteSuccess = (note: AccountingNote) => this.setState({notes: this.state.notes.concat(note)});

	onUpdateNoteSuccess = (note: AccountingNote) => this.setState({
		notes: this.state.notes.filter(n => n.id !== note.id).concat(note),
		noteToEdit: none
	});

	onEditNoteClick = (note: AccountingNote) => this.setState({noteToEdit: option(note)});

	onDeleteNoteSuccess = (note: AccountingNote) => {
		this.setState({noteToEdit: none, notes: this.state.notes.filter(n => n.id !== note.id)});
	};
}

export default withRouter(TransactionDetailsView)
